import React, { useState, useEffect } from 'react';
import './ShopsView.css';
import ApiService from '../../../../services/api';
import { useAuthContext } from '../../../../context/AuthContext';

function ShopsView() {
  const { getToken } = useAuthContext(); // ✅ same as UsersView

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    location: '',
    contact_phone: '',
    category: '',
    is_verified: false,
    is_active: true,
    promo_text: ''
  });

  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: ''
  });

  useEffect(() => {
    fetchShops();
  }, []);

  // Close dropdown on outside click — same pattern as UsersView
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ✅ Uses ApiService.fetchAdminShops(token) — same as fetchUsers(token)
  const fetchShops = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const data = await ApiService.fetchAdminShops(token);
      console.log('✅ Admin - Fetched shops:', data);
      if (data && data.length > 0) {
        console.log('🔍 First shop data:', data[0]);
        console.log('🔍 Owner fields:', {
          owner: data[0].owner,
          owner_email: data[0].owner_email,
          owner_username: data[0].owner_username
        });
      }
      setShops(data);
    } catch (err) {
      console.error('❌ Error fetching shops:', err);
      setError('Failed to load shops. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredShops = shops.filter(shop => {
    if (filters.status !== 'all') {
      const shopStatus = shop.is_verified ? 'approved' : shop.is_active ? 'pending' : 'rejected';
      if (shopStatus !== filters.status) return false;
    }
    if (filters.category !== 'all' && shop.category !== filters.category) return false;
    if (filters.search) {
      const s = filters.search.toLowerCase();
      return (
        shop.name?.toLowerCase().includes(s) ||
        shop.owner_email?.toLowerCase().includes(s) ||
        shop.owner_username?.toLowerCase().includes(s) ||
        shop.location?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatus = (shop) => {
    if (shop.is_verified) return 'approved';
    if (shop.is_active) return 'pending';
    return 'rejected';
  };

  const getCategoryDisplay = (shop) => shop.category_display || shop.category || 'N/A';

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const openViewModal = (shop) => {
    setSelectedShop(shop);
    setShowViewModal(true);
    setActiveDropdown(null);
  };

  const openEditModal = (shop) => {
    setSelectedShop(shop);
    setEditFormData({
      name: shop.name || '',
      location: shop.location || '',
      contact_phone: shop.contact_phone || '',
      category: shop.category || '',
      is_verified: shop.is_verified || false,
      is_active: shop.is_active !== undefined ? shop.is_active : true,
      promo_text: shop.promo_text || ''
    });
    setShowEditModal(true);
    setActiveDropdown(null);
  };

  // ── CRUD handlers — all use ApiService, same pattern as UsersView ────────────

  // ✅ Edit — ApiService.updateAdminShop(id, data, token)  ←→  apiService.updateUser(id, data, token)
  const handleEditShop = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const response = await ApiService.updateAdminShop(selectedShop.id, editFormData, token);
      if (response.success) {
        alert('Shop updated successfully!');
        setShowEditModal(false);
        setSelectedShop(null);
        fetchShops();
      } else {
        alert(response.error || 'Failed to update shop');
      }
    } catch (error) {
      console.error('❌ Error updating shop:', error);
      alert('Failed to update shop');
    }
  };

  // ✅ Approve/Reject — ApiService.approveShop / rejectShop  ←→  apiService.toggleUserStatus
  const handleToggleStatus = async (shop) => {
    const isApproved = shop.is_verified;
    const action = isApproved ? 'reject' : 'approve';
    if (!window.confirm(`Are you sure you want to ${action} this shop?`)) return;

    try {
      const token = getToken();
      const response = isApproved
        ? await ApiService.rejectShop(shop.id, token)
        : await ApiService.approveShop(shop.id, token);

      if (response.success) {
        alert(`Shop ${action}d successfully!`);
        fetchShops();
      } else {
        alert(response.error || `Failed to ${action} shop`);
      }
    } catch (error) {
      console.error(`❌ Error ${action}ing shop:`, error);
      alert(`Failed to ${action} shop`);
    }
    setActiveDropdown(null);
  };

  // ✅ Delete — ApiService.deleteAdminShop(id, token)  ←→  apiService.deleteUser(id, token)
  const handleDelete = async (shop) => {
    if (!window.confirm(`Are you sure you want to delete "${shop.name}"? This cannot be undone.`)) return;
    try {
      const token = getToken();
      const response = await ApiService.deleteAdminShop(shop.id, token);
      if (response.success) {
        alert('Shop deleted successfully!');
        fetchShops();
      } else {
        alert(response.error || 'Failed to delete shop');
      }
    } catch (error) {
      console.error('❌ Error deleting shop:', error);
      alert('Failed to delete shop');
    }
    setActiveDropdown(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="shops-management">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading shops...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shops-management">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchShops} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="shops-management">
      <div className="management-header">
        <div className="header-title">
          <h2>Shops Management</h2>
          <span className="count-badge">{filteredShops.length} shops</span>
        </div>

        <div className="filters">
          <input
            type="text"
            className="search-input"
            placeholder="Search shops, owners, location..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <select
            className="filter-select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            className="filter-select"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="all">All Categories</option>
            <option value="electronics">Electronics</option>
            <option value="fashion">Fashion</option>
            <option value="phone_repair">Phone Repair</option>
            <option value="grocery">Grocery</option>
            <option value="furniture">Furniture</option>
            <option value="restaurant">Restaurant</option>
            <option value="beauty">Beauty</option>
            <option value="other">Other</option>
          </select>
          <button onClick={fetchShops} className="btn-refresh" title="Refresh">🔄</button>
        </div>
      </div>

      {filteredShops.length === 0 ? (
        <div className="empty-state">
          <p>No shops found</p>
          {(filters.search || filters.status !== 'all' || filters.category !== 'all') && (
            <button
              onClick={() => setFilters({ status: 'all', category: 'all', search: '' })}
              className="btn-clear-filters"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>SHOP NAME</th>
                <th>OWNER</th>
                <th>CATEGORY</th>
                <th>LOCATION</th>
                <th>RATING</th>
                <th>ORDERS</th>
                <th>STATUS</th>
                <th>JOINED</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredShops.map((shop) => (
                <tr key={shop.id}>
                  <td>
                    <div className="product-cell">
                      <div className="shop-icon">🏪</div>
                      <div className="shop-info">
                        <span className="product-name">{shop.name}</span>
                        {shop.promo_text && (
                          <span className="shop-promo">{shop.promo_text}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="owner-cell">
                      <span>{shop.owner_email || shop.owner_username || (shop.owner ? `User #${shop.owner}` : 'N/A')}</span>
                      {shop.contact_phone && (
                        <span className="owner-phone">{shop.contact_phone}</span>
                      )}
                    </div>
                  </td>
                  <td><span className="category-badge">{getCategoryDisplay(shop)}</span></td>
                  <td>{shop.location || 'N/A'}</td>
                  <td><div className="rating-cell">⭐ {shop.rating || '0.00'}</div></td>
                  <td><span className="orders-count">{shop.total_orders || 0}</span></td>
                  <td>
                    <span className={`status-badge ${getStatus(shop)}`}>
                      {getStatus(shop)}
                    </span>
                  </td>
                  <td>{formatDate(shop.created_at)}</td>

                  {/* ✅ Three-dot dropdown — identical to UsersView */}
                  <td>
                    <div className="actions-cell">
                      <button
                        className="action-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(activeDropdown === shop.id ? null : shop.id);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="1" fill="currentColor"/>
                          <circle cx="19" cy="12" r="1" fill="currentColor"/>
                          <circle cx="5"  cy="12" r="1" fill="currentColor"/>
                        </svg>
                      </button>

                      {activeDropdown === shop.id && (
                        <div className="action-menu-dropdown">
                          <button onClick={() => openViewModal(shop)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
                              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            View
                          </button>
                          <button onClick={() => openEditModal(shop)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Edit
                          </button>
                          <button onClick={() => handleToggleStatus(shop)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            {shop.is_verified ? 'Reject' : 'Approve'}
                          </button>
                          <button onClick={() => handleDelete(shop)} className="danger">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
                              <path d="M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2"/>
                              <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && selectedShop && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h2>Shop Details</h2>
              <button className="modal-close-btn" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-form-new">
              <div className="form-row-two">
                <div className="form-field">
                  <label>Shop Name</label>
                  <p className="view-value">{selectedShop.name || 'N/A'}</p>
                </div>
                <div className="form-field">
                  <label>Category</label>
                  <p className="view-value">{getCategoryDisplay(selectedShop)}</p>
                </div>
              </div>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Owner</label>
                  <p className="view-value">{selectedShop.owner_email || selectedShop.owner_username || 'N/A'}</p>
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <p className="view-value">{selectedShop.contact_phone || 'N/A'}</p>
                </div>
              </div>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Location</label>
                  <p className="view-value">{selectedShop.location || 'N/A'}</p>
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <p className="view-value">
                    <span className={`status-badge ${getStatus(selectedShop)}`}>
                      {getStatus(selectedShop)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Rating</label>
                  <p className="view-value">⭐ {selectedShop.rating || '0.00'}</p>
                </div>
                <div className="form-field">
                  <label>Total Orders</label>
                  <p className="view-value">{selectedShop.total_orders || 0}</p>
                </div>
              </div>
              {selectedShop.promo_text && (
                <div className="form-field">
                  <label>Promo Text</label>
                  <p className="view-value">{selectedShop.promo_text}</p>
                </div>
              )}
              <div className="form-field">
                <label>Joined</label>
                <p className="view-value">{formatDate(selectedShop.created_at)}</p>
              </div>
              <div className="modal-actions-new">
                <button type="button" className="btn-cancel" onClick={() => setShowViewModal(false)}>Close</button>
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => { setShowViewModal(false); openEditModal(selectedShop); }}
                >
                  Edit Shop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && selectedShop && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h2>Edit Shop</h2>
              <button className="modal-close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form className="modal-form-new" onSubmit={handleEditShop}>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Shop Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Shop name"
                  />
                </div>
                <div className="form-field">
                  <label>Category</label>
                  <select
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  >
                    <option value="">Select category</option>
                    <option value="electronics">Electronics</option>
                    <option value="fashion">Fashion</option>
                    <option value="phone_repair">Phone Repair</option>
                    <option value="grocery">Grocery</option>
                    <option value="furniture">Furniture</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="beauty">Beauty</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Location</label>
                  <input
                    type="text"
                    value={editFormData.location}
                    onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                    placeholder="Shop location"
                  />
                </div>
                <div className="form-field">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={editFormData.contact_phone}
                    onChange={(e) => setEditFormData({ ...editFormData, contact_phone: e.target.value })}
                    placeholder="+254712345678"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Promo Text</label>
                <input
                  type="text"
                  value={editFormData.promo_text}
                  onChange={(e) => setEditFormData({ ...editFormData, promo_text: e.target.value })}
                  placeholder="Optional promotional text"
                />
              </div>
              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="is_verified_edit"
                  checked={editFormData.is_verified}
                  onChange={(e) => setEditFormData({ ...editFormData, is_verified: e.target.checked })}
                />
                <label htmlFor="is_verified_edit">Verified Shop</label>
              </div>
              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="is_active_edit"
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                />
                <label htmlFor="is_active_edit">Active Shop</label>
              </div>
              <div className="modal-actions-new">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-submit">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShopsView;